import {useEffect, useState, useCallback, useRef} from 'react';
import {api} from '../lib/auth.js';

/**
 * One paginated, searchable table, driven by a per-collection column spec.
 *
 * Paging and search happen on the SERVER: vouchers alone is 3 256 documents whose
 * rows push the payload past a megabyte, so filtering in the browser would make the
 * first paint the slowest thing in the app.
 *
 * Typing is debounced — a request per keystroke over a regex query is a good way to
 * make a fast API feel slow — and responses carry a sequence number so a slow early
 * response cannot overwrite a fast later one, which is the classic way a search box
 * ends up showing results for a query the user has already changed.
 *
 * `fieldColumn`, when given, turns every name in the response's `fields` into a column
 * after the fixed ones — how a register shows everything that can be edited on it
 * without the web keeping its own copy of the API's allow-list. `wide` pins the first
 * column so a row stays identifiable while scrolling sideways.
 */
export default function DataTable({collection, columns: fixed, filters = [], onRow, fieldColumn, wide}) {
	const [state, setState] = useState({rows: [], total: 0, page: 1, pages: 1, loading: true});
	const [q, setQ] = useState('');
	const [page, setPage] = useState(1);
	const [filter, setFilter] = useState({});
	const seq = useRef(0);

	useEffect(() => { setPage(1); }, [q, filter, collection]);

	const load = useCallback(async () => {
		const mine = ++seq.current;
		setState(s => ({...s, loading: true}));
		const params = new URLSearchParams({page: String(page), limit: '50'});
		if (q) params.set('q', q);
		for (const [k, v] of Object.entries(filter)) if (v !== '') params.set(k, v);
		try {
			const res = await api(`/api/browse/${collection}?${params}`);
			const data = await res.json();
			if (mine !== seq.current) return;   // a newer request has already landed
			setState({...data, loading: false});
		} catch {
			if (mine === seq.current) setState(s => ({...s, loading: false}));
		}
	}, [collection, q, page, filter]);

	useEffect(() => {
		const t = setTimeout(load, q ? 220 : 0);
		return () => clearTimeout(t);
	}, [load, q]);

	const columns = fieldColumn && state.fields
		? [...fixed, ...state.fields.filter(f => !fixed.some(c => c.key === f)).map(fieldColumn)]
		: fixed;

	return (
		<>
			<div className="toolbar">
				<input className="search" type="search" placeholder="Sök…" value={q}
					onChange={e => setQ(e.target.value)} aria-label="Sök" />
				{filters.map(f => (
					<select key={f.key} className="select" value={filter[f.key] ?? ''}
						onChange={e => setFilter(s => ({...s, [f.key]: e.target.value}))}
						aria-label={f.label}>
						<option value="">{f.label}</option>
						{f.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
					</select>
				))}
				<span className="spacer" />
				<span className="stamp">
					{state.loading ? 'Hämtar…' : `${state.total.toLocaleString('sv-SE')} rader`}
				</span>
				<div className="pager">
					<button className="btn" disabled={page <= 1 || state.loading}
						onClick={() => setPage(p => p - 1)}>Föregående</button>
					<span className="stamp">{state.page} / {state.pages || 1}</span>
					<button className="btn" disabled={page >= state.pages || state.loading}
						onClick={() => setPage(p => p + 1)}>Nästa</button>
				</div>
			</div>
			<div className="table-wrap">
				<table className={wide ? 'table--wide' : undefined}>
					<thead><tr>{columns.map(c => <th key={c.key}>{c.label}</th>)}</tr></thead>
					<tbody>
						{state.rows.length === 0 && !state.loading && (
							<tr><td colSpan={columns.length} style={{textAlign: 'center', padding: 28}}>
								Inga träffar
							</td></tr>
						)}
						{state.rows.map(r => (
							<tr key={r._id} onClick={onRow ? () => onRow(r) : undefined}
								style={onRow ? {cursor: 'pointer'} : undefined}>
								{columns.map(c => (
									<td key={c.key} className={c.strong ? 'num--strong' : undefined}>
										{c.render ? c.render(r) : (r[c.key] ?? '—')}
									</td>
								))}
							</tr>
						))}
					</tbody>
				</table>
			</div>
		</>
	);
}
